import { useCallback, useEffect, useRef, useState } from "react";

export interface PanelPosition {
  x: number;
  y: number;
}

const DEFAULT_POS: PanelPosition = { x: -1, y: -1 };

/**
 * Shared drag behaviour for floating panels (messages panel, itinerary toolbar).
 * Position is stored in localStorage under `storageKey`.
 * `{ x: -1, y: -1 }` means "use the default CSS position".
 */
export const useDraggablePanel = (storageKey: string) => {
  const [position, setPosition] = useState<PanelPosition>(DEFAULT_POS);
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Load saved position
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (typeof parsed?.x === "number" && typeof parsed?.y === "number") {
          setPosition(parsed);
        }
      }
    } catch {
      /* ignore malformed values */
    }
  }, [storageKey]);

  const clamp = useCallback((pos: PanelPosition): PanelPosition => {
    const el = containerRef.current;
    const width = el?.offsetWidth ?? 320;
    const height = el?.offsetHeight ?? 56;
    const maxX = Math.max(0, window.innerWidth - width - 8);
    const maxY = Math.max(0, window.innerHeight - height - 8);
    return {
      x: Math.min(Math.max(8, pos.x), maxX),
      y: Math.min(Math.max(8, pos.y), maxY),
    };
  }, []);

  const persist = useCallback(
    (pos: PanelPosition) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(pos));
      } catch {
        /* storage unavailable */
      }
    },
    [storageKey]
  );

  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    dragOffset.current = { x: clientX - rect.left, y: clientY - rect.top };
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e: MouseEvent | TouchEvent) => {
      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
      setPosition({
        x: clientX - dragOffset.current.x,
        y: clientY - dragOffset.current.y,
      });
    };

    const handleEnd = () => {
      setIsDragging(false);
      setPosition((prev) => {
        const next = clamp(prev);
        persist(next);
        return next;
      });
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("mouseup", handleEnd);
    window.addEventListener("touchend", handleEnd);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging, clamp, persist]);

  // Keep the panel on screen when the window resizes
  useEffect(() => {
    const onResize = () => {
      setPosition((prev) => {
        if (prev.x === -1 && prev.y === -1) return prev;
        const next = clamp(prev);
        persist(next);
        return next;
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [clamp, persist]);

  const reset = useCallback(() => {
    setPosition(DEFAULT_POS);
    try {
      localStorage.removeItem(storageKey);
    } catch {
      /* storage unavailable */
    }
  }, [storageKey]);

  const isCustomPosition = position.x !== -1 || position.y !== -1;

  return {
    containerRef,
    position,
    isCustomPosition,
    isDragging,
    handleDragStart,
    reset,
  };
};
